import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Youtube } from "lucide-react";
import NewsletterForm from "@/components/NewsletterForm";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e1ff2bf4-1540-494b-b229-04ddc96f1193/artifacts/g70ytwbu_1163.jpg";

export const Footer = () => {
  return (
    <footer className="bg-[#0F172A] text-white pb-20 sm:pb-0 relative z-[100]">
      {/* Newsletter Section */}
      <div className="border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold font-['Outfit'] mb-2">Stay Connected</h3>
              <p className="text-slate-400">Subscribe to our newsletter for updates on our mission and events.</p>
            </div>
            <NewsletterForm />
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Column */}
          <div>
            <img 
              src={LOGO_URL} 
              alt="PFCA Logo" 
              className="h-12 w-auto mb-4 bg-white p-2 rounded"
            />
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Peace for Change Alliance Inc. is dedicated to educating, advocating, and mobilizing communities toward a culture of peace.
            </p>
            <div className="flex gap-4">
              <a href="https://www.facebook.com/peaceforchangealliance" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors p-2 -m-2" aria-label="Facebook" data-testid="footer-facebook">
                <Facebook className="w-6 h-6" />
              </a>
              <a href="https://twitter.com/peace4change" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors p-2 -m-2" aria-label="Twitter" data-testid="footer-twitter">
                <Twitter className="w-6 h-6" />
              </a>
              <a href="https://www.instagram.com/peaceforchangealliance" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors p-2 -m-2" aria-label="Instagram" data-testid="footer-instagram">
                <Instagram className="w-6 h-6" />
              </a>
              <a href="https://www.youtube.com/@voicesagainstviolencecoali2074" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors p-2 -m-2" aria-label="YouTube" data-testid="footer-youtube">
                <Youtube className="w-6 h-6" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold font-['Outfit'] text-lg mb-4">Quick Links</h4>
            <ul className="space-y-1">
              <li><Link to="/about" className="footer-link text-sm block py-3 hover:text-white relative z-10" data-testid="footer-about-link">About Us</Link></li>
              <li><Link to="/volunteer" className="footer-link text-sm block py-3 hover:text-white relative z-10" data-testid="footer-volunteer-link">Become a Volunteer</Link></li>
              <li><Link to="/donate" className="footer-link text-sm block py-3 hover:text-white relative z-10" data-testid="footer-donate-link">Make a Donation</Link></li>
              <li><Link to="/events" className="footer-link text-sm block py-3 hover:text-white relative z-10" data-testid="footer-events-link">Upcoming Events</Link></li>
              <li><Link to="/blog" className="footer-link text-sm block py-3 hover:text-white relative z-10" data-testid="footer-blog-link">News & Updates</Link></li>
            </ul>
          </div>

          {/* Programs - Now with links */}
          <div>
            <h4 className="font-semibold font-['Outfit'] text-lg mb-4">Our Programs</h4>
            <ul className="space-y-1">
              <li><Link to="/youth-victory" className="footer-link text-sm block py-3 hover:text-white relative z-10" data-testid="footer-youth-link">Youth Violence Prevention</Link></li>
              <li><Link to="/activism" className="footer-link text-sm block py-3 hover:text-white relative z-10" data-testid="footer-activism-link">Community Outreach</Link></li>
              <li><Link to="/about" className="footer-link text-sm block py-3 hover:text-white relative z-10" data-testid="footer-peace-link">Peace Education</Link></li>
              <li><Link to="/activism" className="footer-link text-sm block py-3 hover:text-white relative z-10" data-testid="footer-advocacy-link">Advocacy Campaigns</Link></li>
              <li><Link to="/veterans" className="footer-link text-sm block py-3 hover:text-white relative z-10" data-testid="footer-support-link">Support Services</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold font-['Outfit'] text-lg mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#0056D2] flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">Milwaukee, Wisconsin</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#0056D2] flex-shrink-0" />
                <a href="tel:414-502-7296" className="text-slate-400 text-sm hover:text-white transition-colors">414-502-7296</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#0056D2] flex-shrink-0" />
                <a href="mailto:tracey.e.dent@gmail.com" className="text-slate-400 text-sm hover:text-white transition-colors">tracey.e.dent@gmail.com</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-700 mt-12 pt-8 pb-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Peace for Change Alliance Inc. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-slate-400 text-sm py-3 hover:text-white transition-colors relative z-10" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" className="text-slate-400 text-sm py-3 hover:text-white transition-colors relative z-10" data-testid="footer-terms-link">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
